import { isArray } from "lodash";
import {
  MainCriterias,
  CaseSubCriterias,
  InvoiceSubCriterias,
  CreditorSubCriterias,
  DebtorSubCriterias,
  COLEN,
} from "constants/globalsearch";
import { doesCaseInclude } from "./DoesCaseInclude";
import { sortFunction } from "./SortFunction";
import { EntityTypeShortForm, CaseTypes } from "us.common/constants";
import { LabeledValue, TabsInfo } from "../Types";
import { IOptions } from "components/SearchBar/Interfaces";
import {
  CaseSearchResult,
  DebtorSearchResult,
} from "reducers/GlobalSearch/Types";

/**
 * @description - Check whether the give id (case no, sub case no, debtor or creditor no) related result is viewed or not
 * @param {Array<string | number>} viewedList
 * @param {string | number} id
 */
export const isViewed = (
  viewedList: Array<string | number>,
  exCaseNo: string | number
): Boolean => {
  try {
    if (viewedList.length > 0) {
      return viewedList.some((viewedItem) => viewedItem == exCaseNo);
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * @description - Get debtor data to update
 * @param {number} debtorEntNo
 * @param {DebtorSearchResult[]} debtorData
 */
export const getDebtorArData = (
  debtorEntNo: number,
  debtorData: DebtorSearchResult[]
): DebtorSearchResult[] => {
  try {
    if (isArray(debtorData)) {
      const data = debtorData.map((debtor: DebtorSearchResult) => {
        if (debtor.debtorEntNo == debtorEntNo) {
          return {
            ...debtor,
            isARCollapsed: !debtor.isARCollapsed,
          };
        } else {
          return { ...debtor };
        }
      });
      return data;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description - Get tab state history data
 * @param {TabsInfo} tabState - Current tab state
 * @param {any} backNavigationData - Back navigation state data
 * @returns {TabsInfo}
 */
export const getTabStateHolderHistory = (
  tabState: TabsInfo,
  backNavigationData: any
): TabsInfo => {
  try {
    if (tabState && backNavigationData) {
      const { activeTab, filters, currentHit } = backNavigationData;
      switch (activeTab) {
        case MainCriterias.INVOICE:
          return {
            ...tabState,
            invoiceTab: {
              ...tabState.invoiceTab,
              hitValue: currentHit,
            },
            selectedTab: activeTab,
            filterValues: filters,
          };
        case MainCriterias.CASE:
          return {
            ...tabState,
            caseTab: {
              ...tabState.caseTab,
              hitValue: currentHit,
            },
            selectedTab: activeTab,
            filterValues: filters,
          };
        case MainCriterias.DEBTOR:
          return {
            ...tabState,
            debtorTab: {
              ...tabState.debtorTab,
              hitValue: currentHit,
            },
            selectedTab: activeTab,
            filterValues: filters,
          };
        case MainCriterias.CREDITOR:
          return {
            ...tabState,
            creditorTab: {
              ...tabState.creditorTab,
              hitValue: currentHit,
            },
            selectedTab: activeTab,
            filterValues: filters,
          };
        default:
          return {
            ...tabState,
            selectedTab: activeTab,
            filterValues: filters,
          };
      }
    } else {
      return tabState;
    }
  } catch (error) {
    return tabState;
  }
};

/* @description - Filter and sort main search result by tab data, sorting and filtering values
 * @param {any[]} data - tab results
 * @param {any} filterValues - filter values
 * @param {any} sortKey - sort key
 * @param {any} sortOrder - ASC or DESC (1 and 0)
 */
export const filterTabResult = (
  data: any[],
  filterValues: any,
  sortKey: any,
  sortOrder: any
) => {
  try {
    if (isArray(data)) {
      const filteredData = data
        .filter((caseL: CaseSearchResult) =>
          doesCaseInclude(caseL, filterValues)
        )
        .sort((value1: { [x: string]: any }, value2: { [x: string]: any }) =>
          sortFunction(value1[sortKey], value2[sortKey], sortOrder)
        );
      return filteredData;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description - Change the current tab in archive view if theres no available data
 * @param {any} parsedQueryValue - Query value
 * @param {MainCriterias} selectedTab - Current selected tab
 * @param {Array} invoiceSearchResults - Invoice list
 * @param {Array} caseSearchResults - Case list
 * @returns {MainCriterias} - Updated tab key
 */
export const getVisibleTabInArchiveView = (
  parsedQueryValue: any,
  selectedTab: MainCriterias,
  invoiceSearchResults: Array<any>,
  caseSearchResults: Array<any>
): MainCriterias => {
  try {
    if (
      !parsedQueryValue?.cat &&
      invoiceSearchResults?.length == 0 &&
      caseSearchResults?.length > 0 &&
      selectedTab == MainCriterias.INVOICE
    ) {
      return MainCriterias.CASE;
    } else if (
      !parsedQueryValue?.cat &&
      invoiceSearchResults?.length > 0 &&
      caseSearchResults?.length == 0 &&
      selectedTab == MainCriterias.CASE
    ) {
      return MainCriterias.INVOICE;
    } else {
      return selectedTab;
    }
  } catch (error) {
    return selectedTab;
  }
};

/**
 * Get box url for the company
 * @param config - Company configuration
 * @param company - Company name
 * @returns Box URL for the company
 */
export const getBoxUrl = (config: Array<any>, company: string): string => {
  try {
    const companyConfig = config.find((comp) => comp.companyName === company);
    return companyConfig ? companyConfig.boxWebUrl : "";
  } catch (error) {
    return "";
  }
};

/**
 * @function
 * @description If the object has a length greater than 0, return the object, otherwise return an empty array.
 * @param {any} data - any
 * @returns An array of any type or an empty array.
 */
export const getData = (data: any): any[] | [] => {
  try {
    return Object.keys(data).length > 0 ? data : [];
  } catch (e) {
    return [];
  }
};

/**
 * @function
 * @description Format archived search results
 * @param {any[]} data - endpoint response data
 * @returns Formatted data
 */
export const formatArchiveSearchResult = (data: Array<any>): object => {
  try {
    if (Array.isArray(data)) {
      const caseList: any = data?.filter(
        ({ caseType }: any) => caseType == EntityTypeShortForm.CASE
      );
      const invoiceList: any = data?.filter(
        ({ caseType }: any) => caseType != EntityTypeShortForm.CASE
      );

      return {
        caseList: caseList?.map((caseRecord: any) => {
          const { lastActivityName } = caseRecord ?? {};
          return {
            ...caseRecord,
            caseType: CaseTypes.CLOSED,
            lastMessageActivityName: lastActivityName,
          };
        }),
        invoiceList: invoiceList?.map((invoiceRecord: any) => {
          const { caseState, lastActivityName, invoiceRef } =
            invoiceRecord ?? {};
          return {
            ...invoiceRecord,
            invoiceNumber: invoiceRef,
            caseType: CaseTypes.CLOSED,
            subCaseState: caseState,
            lastMessageActivityName: lastActivityName,
          };
        }),
      };
    } else {
      return {
        caseList: [],
        invoiceList: [],
      };
    }
  } catch (e) {
    return {
      caseList: [],
      invoiceList: [],
    };
  }
};

/**
 * @function
 * @description It takes an enum and returns an array of objects with the enum's values as the object's properties
 * @returns An array of objects with the following properties:
 * label: string
 * value: string
 * key: string
 */
export const getCaseSubCriterias = (): LabeledValue<CaseSubCriterias>[] => {
  try {
    return (Object.values(CaseSubCriterias) as Array<CaseSubCriterias>).map(
      (value: any) => ({
        label: value,
        value: value,
        key: value,
      })
    );
  } catch (e) {
    return [];
  }
};

/**
 * @function
 * @description Check if the date is a valid date in the format yyyy/mm/dd
 * @param dateString string containing a date representation
 * @returns if the dateString holds a valid date
 */
export const isValidDate = (dateString: string): boolean => {
  try {
    const regEx = /^\d{4}\/\d{2}\/\d{2}$/;
    if (!dateString.match(regEx)) return false; // Invalid format
    const d = new Date(dateString);
    const dNum = d.getTime();
    if (!dNum && dNum !== 0) return false; // NaN value, Invalid date
    const [year, month, date] = dateString.split("/");
    return (
      d.getDate() === +date &&
      d.getMonth() + 1 === +month &&
      d.getFullYear() === +year
    );
  } catch (e) {
    return false;
  }
};

/**
 * @function
 * @description It takes an enum and returns an array of objects with the enum values as the label, value, and key
 * @returns An array of objects with the following properties:
 * label: string
 * value: string
 * key: string
 */
export const getMainSearchCriterias = (): LabeledValue<MainCriterias>[] => {
  try {
    return (Object.values(MainCriterias) as Array<MainCriterias>).map(
      (value) => ({
        label: value,
        value: value,
        key: value,
      })
    );
  } catch (e) {
    return [];
  }
};

/**
 * @function
 * @description It takes an enum and returns an array of objects with the enum values as the label, value, and key
 * @returns An array of objects with the following properties:
 * label: string
 * value: string
 * key: string
 */
export const getInvoiceSubCategories =
  (): LabeledValue<InvoiceSubCriterias>[] => {
    try {
      return (
        Object.values(InvoiceSubCriterias) as Array<InvoiceSubCriterias>
      ).map((value) => ({
        label: value,
        value: value,
        key: value,
      }));
    } catch (e) {
      return [];
    }
  };

/**
 * @function
 * @description Called in the first rendering of the searh autocomplete options
 * @param selectedCriteria the main criteria selected, which is undefined
 * @param optionalCriterias it is - MainCriteriasEnum in this case
 * @param inputValue the user entered value
 * @returns the list of options for autocomplete (not including or handling the history)
 */
export const getInitialDropDownOptionValues = <
  T extends LabeledValue<
    | MainCriterias
    | InvoiceSubCriterias
    | CaseSubCriterias
    | CreditorSubCriterias
    | DebtorSubCriterias
  >
>(
  selectedCriteria: LabeledValue<MainCriterias> | undefined,
  optionalCriterias: T[],
  inputValue: string
): IOptions[] => {
  try {
    return (optionalCriterias as any).map((option: T) => {
      return {
        label: `${selectedCriteria ? selectedCriteria.label + COLEN + " " : ""
          }${option.label + COLEN} ${inputValue}`,
        value: `${selectedCriteria ? selectedCriteria.value + COLEN + " " : ""
          }${option.value + COLEN} ${inputValue}`,
        key: option.value + COLEN,
      };
    });
  } catch (e) {
    return [];
  }
};

/**
 * @function
 * @description Dropdown options
 * @param selectedCriteria can only be one of MainCriteriasEnum or can be undefined in searching through the main criterias
 * @param optionalCriterias can be one of T
 * @param inputValue the user typed value, do not include the Enum value
 * @param preVals the previous values of the Autocomplete
 * @returns The full Autocomplete dropdown including the history
 */
export const getDropDownOptionValues = <
  T extends LabeledValue<
    | MainCriterias
    | InvoiceSubCriterias
    | CaseSubCriterias
    | CreditorSubCriterias
    | DebtorSubCriterias
  >
>(
  selectedCriteria: LabeledValue<MainCriterias> | undefined,
  optionalCriterias: T[],
  inputValue: string,
  preVals: any
): any => {
  try {
    return {
      ...preVals,
      options: (optionalCriterias as any)
        .map((option: T) => {
          return {
            label: `${selectedCriteria ? selectedCriteria.label + COLEN + " " : ""
              }${option.label + COLEN} ${inputValue}`,
            value: `${selectedCriteria ? selectedCriteria.value + COLEN + " " : ""
              }${option.value + COLEN} ${inputValue}`,
            key: option.value + COLEN,
          };
        })
        .concat(
          preVals.options.filter(
            (optionLine: any) => optionLine.key === "hisotoryBar"
          )
        ),
      value: inputValue,
    };
  } catch (e) {
    return preVals;
  }
};

/**
 * @function
 * @description It takes an enum and returns an array of objects with the enum's values as the object's properties
 * @returns An array of objects with the following properties:
 * label: string
 * value: DebtorSubCriterias
 * key: DebtorSubCriterias
 */
export const getDebtorSubCriterias = (): LabeledValue<DebtorSubCriterias>[] => {
  try {
    return (Object.values(DebtorSubCriterias) as Array<DebtorSubCriterias>).map(
      (value: any) => ({
        label: value,
        value: value,
        key: value,
      })
    );
  } catch (e) {
    return [];
  }
};

/**
 * @function
 * @description It takes an enum and returns an array of objects with the enum's values as the label, value, and key
 * @returns An array of objects with the following properties:
 * label: string
 * value: string
 * key: string
 */
export const getCreditorSubCriterias =
  (): LabeledValue<CreditorSubCriterias>[] => {
    try {
      return (
        Object.values(CreditorSubCriterias) as Array<CreditorSubCriterias>
      ).map((value: any) => ({
        label: value,
        value: value,
        key: value,
      }));
    } catch (e) {
      return [];
    }
  };
